import { put, call } from 'redux-saga/effects';
import { userActions } from '../redux/userSlice';
import { organizationActions } from '../../organization/redux/organizationSlice';
import { getCompanyInfoFromServerFormat } from '../../organization/saga/manager';
import {
    deleteUserMailbox,
    fetchCompanyBaseUsers,
    fetchMyUser,
    postConnectUserGmail,
    postConnectUserOutlook,
} from '../../apis/user';
import {
    getBaseUsersFromServerUsersFormat,
    getUserFromServerUserFormat,
} from './manager';
import i18next from 'i18next';
import { Directions, IUser, LANGUAGES, SnackbarUtils } from '../../../common';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    IDeleteUserMailboxAction,
    IPostConnectUserGmailAction,
    IPostConnectUserOutlookAction,
    IUpdateLanguage,
} from '../redux/interface';
import {
    connectToSocket,
    subscribeToChannel,
    isToolsPage,
} from '../../../base';

export function* fetchMyUserSaga() {
    try {
        const { response } = yield call(fetchMyUser);
        if (response?.status === 200) {
            const myUser: IUser = getUserFromServerUserFormat(response.data);
            yield put(
                userActions.setMyUser({
                    myUser,
                })
            );
            if (myUser?.socketChannels?.length > 0) {
                subscribeToChannel(myUser.socketChannels);
            } else if (isToolsPage()) {
                connectToSocket();
            }
            if (response.data?.company) {
                yield put(
                    organizationActions.setCompanyInfo({
                        companyInfo: getCompanyInfoFromServerFormat(
                            response.data?.company
                        ),
                    })
                );
            }
            const defaultLanguage =
                response.data?.default_language ||
                response.data?.company.default_language;
            yield call(handleLanguage, defaultLanguage);
        } else {
            yield put(userActions.fetchMyUserFailed());
            console.log('Failed loading MyUser'); //TODO: Saga error handler
        }
    } catch (error) {
        yield put(userActions.fetchMyUserFailed());
        console.log(error); //TODO: Saga error handler
    }
}

export function* fetchCompanyBaseUsersSaga() {
    try {
        const { response } = yield call(fetchCompanyBaseUsers);
        if (response?.status === 200) {
            yield put(
                userActions.setCompanyBaseUsers({
                    users: getBaseUsersFromServerUsersFormat(response.data),
                })
            );
        } else {
            console.log('Failed loading company base users'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    }
}

export function* updateLanguageSaga(action: PayloadAction<IUpdateLanguage>) {
    const { language } = action.payload;
    yield call(handleLanguage, language);
}

export function* handleLanguage(language: string) {
    i18next.changeLanguage(language.toLowerCase());
    if (language && language === LANGUAGES.HE) {
        yield put(userActions.setPageDirection({ direction: Directions.rtl }));
        document.documentElement.setAttribute('dir', 'rtl');
    } else {
        yield put(userActions.setPageDirection({ direction: Directions.ltr }));
        document.documentElement.setAttribute('dir', 'ltr');
    }
}

export function* postConnectUserGmailSaga(
    action: PayloadAction<IPostConnectUserGmailAction>
) {
    try {
        const { response } = yield call(postConnectUserGmail, action.payload);
        if (response?.status === 200) {
            yield put(userActions.postConnectUserGmailDone());
            yield put(userActions.fetchMyUser());
            SnackbarUtils.success(
                i18next.t('settings.emailSettings.emailConnectedSuccess')
            );
        }
    } catch (error) {
        console.log(error);
        SnackbarUtils.error(
            i18next.t('settings.emailSettings.emailConnectedError')
        );
    } finally {
        yield put(userActions.postConnectUserGmailDone());
    }
}

export function* postConnectUserOutlookSaga(
    action: PayloadAction<IPostConnectUserOutlookAction>
) {
    try {
        const { response } = yield call(
            postConnectUserOutlook,
            action.payload
        );
        if (response?.status === 200) {
            yield put(userActions.postConnectUserOutlookDone());
            yield put(userActions.fetchMyUser());
            SnackbarUtils.success(
                i18next.t('settings.emailSettings.emailConnectedSuccess')
            );
        }
    } catch (error) {
        console.log(error);
        SnackbarUtils.error(
            i18next.t('settings.emailSettings.emailConnectedError')
        );
    } finally {
        yield put(userActions.postConnectUserOutlookDone());
    }
}

export function* deleteUserMailboxSaga(
    action: PayloadAction<IDeleteUserMailboxAction>
) {
    try {
        const { response } = yield call(deleteUserMailbox, action.payload);
        if (response?.status === 200) {
            yield put(userActions.fetchMyUser());
            SnackbarUtils.success(
                i18next.t('settings.emailSettings.emailRemovedSuccess')
            );
        }
    } catch (error) {
        console.log(error);
        SnackbarUtils.error(
            i18next.t('settings.emailSettings.emailRemovedError')
        );
    } finally {
        yield put(userActions.deleteUserMailboxDone());
    }
}
